import React from 'react';
import { Device, DeviceStatus } from '../../types/device';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import { Settings, Power, History, AlertTriangle, RefreshCw, Shield, Download, Key, RefreshCcw } from 'lucide-react';
import { Button } from '../../../../components/ui/button';

interface DeviceManagementModalProps {
  device: Device;
  onStatusChange: (status: DeviceStatus) => void;
  onReboot: () => void;
  onFirmwareUpdate: () => void;
}

const DeviceManagementModal: React.FC<DeviceManagementModalProps> = ({
  device,
  onStatusChange,
  onReboot,
  onFirmwareUpdate,
}) => {
  const [activeTab, setActiveTab] = React.useState('info');

  const DEVICE_TABS_CONFIG = {
    info: {
      label: 'Information',
      icon: Settings,
      content: () => (
        <div className="grid grid-cols-2 gap-4 p-4">
          <div>
            <h3 className="font-semibold mb-2">Edge Device Details</h3>
            <div className="space-y-2 text-sm">
              <p><span className="font-medium">ID:</span> {device.id}</p>
              <p><span className="font-medium">Name:</span> {device.name}</p>
              <p><span className="font-medium">Status:</span> {device.status}</p>
              <p><span className="font-medium">Firmware:</span> {device.metadata.firmware}</p>
              <p><span className="font-medium">Installation Date:</span> {device.metadata.installationDate}</p>
            </div>
          </div>
          {device.connectedEquipment && (
            <div>
              <h3 className="font-semibold mb-2">Connected Equipment</h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Model:</span> {device.connectedEquipment.model}</p>
                <p><span className="font-medium">Serial Number:</span> {device.connectedEquipment.serialNumber}</p>
                <p><span className="font-medium">Type:</span> {device.connectedEquipment.type}</p>
              </div>
            </div>
          )}
        </div>
      )
    },
    control: {
      label: 'Control',
      icon: Power,
      content: () => (
        <div className="p-4 space-y-4">
          <h3 className="font-semibold mb-4">Device Control</h3>
          <div className="flex flex-col gap-4">
            <Button
              style={{width: '200px'}}
              onClick={onReboot}
              className="bg-[#e04530] text-white hover:bg-[#e04530]/90 flex items-center gap-2 min-w-[100px] justify-center px-4 py-2 rounded-lg"
            >
              <RefreshCw className="h-4 w-4" />
              Reboot Device
            </Button>
            <Button
              style={{width: '200px'}}
              onClick={() => onStatusChange(device.status === 'connected' ? 'disconnected' : 'connected')}
              className="bg-[#e04530] text-white hover:bg-[#e04530]/90 flex items-center gap-2 min-w-[100px] justify-center px-4 py-2 rounded-lg"
            >
              <Power className="h-4 w-4" />
              {device.status === 'connected' ? 'Disable Device' : 'Enable Device'}
              </Button>
          </div>
        </div>
      )
    },
    firmware: {
      label: 'Firmware',
      icon: Download,
      content: () => (
        <div className="p-4 space-y-4">
          <h3 className="font-semibold mb-4">Firmware Management</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Current Version</p>
                <p className="text-sm text-gray-600">{device.metadata.firmware}</p>
              </div>
              <Button 
                onClick={onFirmwareUpdate}
               variant="outline"
                size="sm"
                className="flex items-center gap-2 min-w-[140px] justify-center"
              >
                <RefreshCw className="h-4 w-4" />
                Check for Updates
              </Button>
            </div>
            <div className="text-sm text-gray-600">
              <h4 className="font-medium mb-2">Update History</h4>
              <div className="space-y-2">
                <p>v2.1.0 - Updated on 2024-03-15</p>
                <p>v2.0.0 - Updated on 2024-02-01</p>
                <p>v1.9.0 - Updated on 2024-01-15</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    certificates: {
      label: 'Certificates',
      icon: Shield,
      content: () => (
        <div className="p-4 space-y-4">
          <h3 className="font-semibold mb-4">Security Certificates</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Current Certificate</p>
                <p className="text-sm text-gray-600">Valid until: 2024-12-31</p>
              </div>
              <Button  className="bg-[#e04530] text-white hover:bg-[#e04530]/90 flex items-center gap-2 min-w-[100px] justify-center px-4 py-2 rounded-lg">
                <Key className="h-4 w-4" />
                Renew Certificate
              </Button>
            </div>
          </div>
        </div>
      )
    },
    logs: {
      label: 'Logs',
      icon: History,
      content: () => (
        <div className="p-4 space-y-4">
          <h3 className="font-semibold mb-4">System Logs</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Recent Activity</p>
                <p className="text-sm text-gray-600">Last 24 hours</p>
              </div>
              <Button className="bg-[#e04530] text-white hover:bg-[#e04530]/90 flex items-center gap-2 min-w-[100px] justify-center px-4 py-2 rounded-lg">
                <RefreshCcw className="h-4 w-4" />
                Refresh Logs
              </Button>
            </div>
            <div className="text-sm text-gray-600">
              <div className="space-y-2">
                <p>[2024-03-20 10:15:23] System startup complete</p>
                <p>[2024-03-20 09:45:12] Firmware check completed</p>
                <p>[2024-03-20 09:30:00] Device initialized</p>
              </div>
            </div>
          </div>
        </div>
      )
    }
  } as const;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button 
        variant="outline"
        size="sm"
        className="flex items-center gap-2 min-w-[140px] justify-center"
        >
          <Settings className="h-4 w-4" />
          Manage
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Manage Edge Device</DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="sticky top-0 bg-white rounded-t-lg z-10 border-b border-gray-200">
            <TabsList className="h-12 w-full justify-start gap-8 border-b border-gray-200 bg-transparent px-4 py-8">
              {Object.entries(DEVICE_TABS_CONFIG).map(([key, { label, icon: Icon }]) => (
                <TabsTrigger
                  key={key}
                  value={key}
                    className="data-[state=active]:border-[#e04530] data-[state=active]:text-[#e04530] relative px-1 pb-3 pt-2 text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px]"
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    {label}
                  </div>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {Object.entries(DEVICE_TABS_CONFIG).map(([key, { content: Content }]) => (
            <TabsContent key={key} value={key} className="overflow-y-auto">
              <div className="flex flex-col">
                <div className="bg-white px-8 py-6">
                  <Content />
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default DeviceManagementModal; 